import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import Protected from "@/components/Protected";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Marketplace from "@/pages/Marketplace";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderSuccess from "@/pages/OrderSuccess";
import Stores from "@/pages/Stores";
import Storefront from "@/pages/Storefront";
import ServicesPage from "@/pages/Services";
import FAQ from "@/pages/FAQ";
import Guides from "@/pages/Guides";
import HelpCenter from "@/pages/HelpCenter";
import { About, Contact } from "@/pages/StaticPages";
import Legal from "@/pages/Legal";

import Overview from "@/pages/dashboard/Overview";
import Products from "@/pages/portal/Products";
import OrdersPortal from "@/pages/portal/Orders";
import StoreBuilder from "@/pages/portal/StoreBuilder";
import CRM from "@/pages/portal/CRM";
import AIMarketing from "@/pages/portal/AIMarketing";
import { EmailCampaigns, SMSCampaigns } from "@/pages/portal/Campaigns";
import Affiliate from "@/pages/portal/Affiliate";
import Wallet from "@/pages/portal/Wallet";
import { ProviderServices, Leads } from "@/pages/portal/ServicesAndLeads";
import Fulfillment from "@/pages/portal/Fulfillment";
import Coupons from "@/pages/portal/Coupons";
import Analytics from "@/pages/portal/Analytics";
import Settings from "@/pages/portal/Settings";
import { AdminUsers, AdminPayments, AdminContent, AdminHealth } from "@/pages/portal/AdminPages";
import { AppGuides, AppHelp, MyLeads } from "@/pages/portal/AppExtras";
import GemExchange from "@/pages/portal/GemExchange";
import ExchangeQueue from "@/pages/portal/ExchangeQueue";

import { useAuth } from "@/lib/auth";

import { api } from "@/lib/api";

function AffiliateRedirect() {
  const { code } = useParams();
  React.useEffect(() => {
    api.post(`/affiliate/links/${code}/click`).catch(() => {});
    sessionStorage.setItem("vendora_aff", code);
  }, [code]);
  return <Navigate to="/marketplace" replace />;
}

function LeadsRoleAware() {
  const { user } = useAuth();
  if (user?.roles?.includes("service_provider") || user?.roles?.includes("super_admin")) return <Leads />;
  return <MyLeads />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster richColors position="top-right" />
        <Routes>
          {/* Public */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order/:id/success" element={<OrderSuccess />} />
          <Route path="/stores" element={<Stores />} />
          <Route path="/store/:slug" element={<Storefront />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/guides" element={<Guides />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/pricing" element={<Navigate to="/#pricing" replace />} />
          <Route path="/legal/terms" element={<Legal slug="terms" />} />
          <Route path="/legal/privacy" element={<Legal slug="privacy" />} />
          <Route path="/legal/cookies" element={<Legal slug="cookies" />} />
          <Route path="/legal/refund" element={<Legal slug="refund" />} />
          <Route path="/legal/shipping" element={<Legal slug="shipping" />} />
          <Route path="/legal/vendor" element={<Legal slug="vendor" />} />
          <Route path="/legal/supplier" element={<Legal slug="supplier" />} />
          <Route path="/legal/affiliate" element={<Legal slug="affiliate" />} />
          <Route path="/legal/service-provider" element={<Legal slug="serviceprovider" />} />
          <Route path="/r/:code" element={<AffiliateRedirect />} />

          {/* Authenticated */}
          <Route path="/app" element={<Protected><Overview /></Protected>} />
          <Route path="/app/products" element={<Protected roles={["supplier", "merchant"]}><Products /></Protected>} />
          <Route path="/app/orders" element={<Protected><OrdersPortal /></Protected>} />
          <Route path="/app/store" element={<Protected roles={["merchant", "creator"]}><StoreBuilder /></Protected>} />
          <Route path="/app/crm" element={<Protected roles={["merchant", "supplier", "creator"]}><CRM /></Protected>} />
          <Route path="/app/marketing/ai" element={<Protected><AIMarketing /></Protected>} />
          <Route path="/app/marketing/email" element={<Protected><EmailCampaigns /></Protected>} />
          <Route path="/app/marketing/sms" element={<Protected><SMSCampaigns /></Protected>} />
          <Route path="/app/affiliate" element={<Protected><Affiliate /></Protected>} />
          <Route path="/app/wallet" element={<Protected><Wallet /></Protected>} />
          <Route path="/app/gems" element={<Protected><GemExchange /></Protected>} />
          <Route path="/app/exchange/queue" element={<Protected roles={["exchange_merchant"]}><ExchangeQueue /></Protected>} />
          <Route path="/app/services" element={<Protected roles={["service_provider"]}><ProviderServices /></Protected>} />
          <Route path="/app/leads" element={<Protected><LeadsRoleAware /></Protected>} />
          <Route path="/app/fulfillment" element={<Protected roles={["fulfillment_partner"]}><Fulfillment /></Protected>} />
          <Route path="/app/coupons" element={<Protected roles={["merchant", "supplier"]}><Coupons /></Protected>} />
          <Route path="/app/analytics" element={<Protected><Analytics /></Protected>} />
          <Route path="/app/settings" element={<Protected><Settings /></Protected>} />
          <Route path="/app/guides" element={<Protected><AppGuides /></Protected>} />
          <Route path="/app/help" element={<Protected><AppHelp /></Protected>} />
          <Route path="/app/admin/users" element={<Protected roles={["super_admin"]}><AdminUsers /></Protected>} />
          <Route path="/app/admin/payments" element={<Protected roles={["super_admin"]}><AdminPayments /></Protected>} />
          <Route path="/app/admin/content" element={<Protected roles={["super_admin"]}><AdminContent /></Protected>} />
          <Route path="/app/admin/health" element={<Protected roles={["super_admin"]}><AdminHealth /></Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
